import type { PrescribedTherapyOptionDto } from "../api-client";

/**
 * Folds case, umlauts/ß, punctuation and whitespace runs.
 *
 * MUST stay behaviourally identical to `normalizeHeilmittelText` in the
 * backend's PrescribedTherapy.entity.ts: that function decides whether typed
 * text RESOLVES on save, this one decides whether it MATCHES while typing. If
 * they diverge, the field either hides options the server would have accepted,
 * or offers ones it will reject.
 */
export const normalizeHeilmittelText = (text: string): string =>
  text
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[.,;:()[\]]/g, " ")
    .replace(/[-–—/]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Every string that should match an option: both labels plus every alias. */
export const optionHaystack = (option: PrescribedTherapyOptionDto): string[] =>
  [option.shortLabel ?? "", option.label, option.id, ...(option.aliases ?? [])].filter(Boolean);

/**
 * Does typed text match this term? Normalized substring against labels, id and
 * aliases — so "bobath" finds KG-ZNS nach Bobath, and "kg zns" finds "KG-ZNS"
 * despite the hyphen.
 */
export const matchesPrescribedTherapy = (
  option: PrescribedTherapyOptionDto,
  query: string
): boolean => {
  const needle = normalizeHeilmittelText(query);
  if (!needle) return true;
  return optionHaystack(option).some((h) => normalizeHeilmittelText(h).includes(needle));
};

/**
 * Rank matches so the best one is first (and Enter picks something sensible):
 * exact hit on a display form, then prefix, then anything. Ties keep the
 * server's alphabetical order.
 */
export const rankPrescribedTherapyMatch = (
  option: PrescribedTherapyOptionDto,
  query: string
): number => {
  const needle = normalizeHeilmittelText(query);
  if (!needle) return 3;
  const primary = [option.shortLabel ?? "", option.label, option.id]
    .filter(Boolean)
    .map(normalizeHeilmittelText);
  const all = optionHaystack(option).map(normalizeHeilmittelText);
  if (primary.some((h) => h === needle)) return 0;
  if (all.some((h) => h === needle)) return 1;
  if (all.some((h) => h.startsWith(needle))) return 2;
  return 3;
};

/** The option list for a query: filtered, then ranked best-first. */
export const filterPrescribedTherapyOptions = (
  options: PrescribedTherapyOptionDto[],
  query: string
): PrescribedTherapyOptionDto[] =>
  options
    .filter((o) => matchesPrescribedTherapy(o, query))
    .map((o, index) => ({ o, index, rank: rankPrescribedTherapyMatch(o, query) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ o }) => o);

/**
 * The text to store for a chosen term. `shortLabel` is what the practice
 * types and reads, and it is a registered alias for its own term, so it
 * round-trips through the backend's resolver.
 */
export const prescribedTherapyInputValue = (option: PrescribedTherapyOptionDto): string =>
  option.shortLabel || option.label || option.id;
